import Autocomplete from "@mui/material/Autocomplete"
import CircularProgress from "@mui/material/CircularProgress"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import { useEffect, useState } from "react"
import { useDebounceValue } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CandidateDropdownOption from "./CandidateDropdownOption"

export default function SelectCandidateAutocomplete({
  selectedCandidate,
  setSelectedCandidate,
  searchText,
  setSearchText,
  autoCompleteProps = {},
  textFieldProps = {},
}) {
  const [candidates, setCandidates] = useState([])
  const debouncedSearchText = useDebounceValue(searchText)[0]
  const [isLoading, setIsLoading] = useState(false)
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!debouncedSearchText) {
        setCandidates([])
        return
      }

      setIsLoading(true)

      const urlSearchParams = new URLSearchParams()
      urlSearchParams.append("q", debouncedSearchText)

      axios
        .get(`/crm/api/offers/create/candidates/?${urlSearchParams.toString()}`)
        .then(({ data }) => setCandidates(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchText]
  )

  return (
    <Autocomplete
      inputValue={searchText}
      onInputChange={(e, newValue) => setSearchText(newValue)}
      value={selectedCandidate}
      onChange={(e, newValue) => setSelectedCandidate(newValue)}
      options={candidates}
      renderInput={(props) => (
        <TextField
          label="Select candidate"
          size="medium"
          {...props}
          InputProps={{
            ...props.InputProps,
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress
                  variant="indeterminate"
                  size={16}
                  thickness={4}
                />
              </InputAdornment>
            ) : null,
          }}
          {...textFieldProps}
        />
      )}
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => option.candidate_name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <CandidateDropdownOption props={props} option={option} />
      )}
      noOptionsText={
        searchText
          ? "No candidates found matching this query"
          : "Type to search..."
      }
      {...autoCompleteProps}
    />
  )
}

export function useSelectCandidateAutocompleteState() {
  const [searchText, setSearchText] = useState("")
  const [selectedCandidate, setSelectedCandidate] = useState(null)

  function clearSelectCandidateAutocomplete() {
    setSearchText("")
    setSelectedCandidate(null)
  }

  return {
    selectedCandidate,
    setSelectedCandidate,
    searchText,
    setSearchText,
    clearSelectCandidateAutocomplete,
  }
}
